"use client"

import { handleNumericInput } from "@/utils/utils"
import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, TextField } from "@mui/material"
import { useTranslations } from "next-intl"
import { LegacyRef, useRef } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { useForm } from "react-hook-form"
import { mediaQueries } from "ui/theme/theme"
import {
  AccidentReportFormData,
  accidentReportDefaultValues,
  accidentReportValidationSchema,
} from "../../../utils/Form/accidentReportValidationSchema"
import ButtonWithArrow from "../../common/bit/ButtonWithArrow"

const UMBRACO__FORMS__FIELDTYPES__RECAPTCHA2__PUBLICKEY =
  process.env.UMBRACO__FORMS__FIELDTYPES__RECAPTCHA2__PUBLICKEY

const StyledForm = styled.form`
  width: 100%;
`

const StyledGrid = styled(Box)`
  display: grid;
  gap: 24px;
  ${mediaQueries.desktopM} {
    grid-template-columns: repeat(2, 1fr);
    column-gap: 20px;
  }
`

const StyledTextField = styled(TextField)``
const StyledButtonContainer = styled(Box)`
  ${mediaQueries.mobile} {
    width: 100%;
  }
  display: flex;
  justify-content: center;
  margin-top: 32px;
`
const StyledDescriptionFieldsContainer = styled(Box)`
  margin-top: 24px;
  display: flex;
  gap: 24px;
  flex-direction: column;
  .MuiInputBase-root {
    padding: 0px;
  }
  .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-multiline {
    height: auto;
    min-height: 80px;
    width: 100%;
    textarea {
      padding: 10px;
    }
  }
`

const StyledButtonWithArrow = styled(ButtonWithArrow)`
  width: 100%;
  ${mediaQueries.desktopM} {
    width: fit-content;
  }
`

interface AccidentReportFormProps {
  data: any
  onSubmit: (formData: AccidentReportFormData) => void
  isSubmitting: boolean
  disabled?: boolean
}

const AccidentReportForm = ({
  data,
  onSubmit,
  isSubmitting,
  disabled = false,
}: AccidentReportFormProps) => {
  const reCaptchaRef = useRef<ReCAPTCHA>()
  const t = useTranslations("ThirdPartyCoverage")

  // Initialize react-hook-form with imported validation schema
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AccidentReportFormData>({
    resolver: yupResolver(accidentReportValidationSchema),
    defaultValues: accidentReportDefaultValues,
  })

  const onFormSubmit = async (data: AccidentReportFormData) => {
    const recaptchaResponse =
      (await reCaptchaRef?.current?.executeAsync()) as string
    const formDataWithRecaptcha = {
      ...data,
      gRecaptchaResponse: recaptchaResponse,
    }
    onSubmit(formDataWithRecaptcha)
  }

  const onInputHandle = (e: any) => {
    const input = e.target as HTMLInputElement
    input.value = input.value.replace(/[^\u0590-\u05FF\s]/g, "")
  }

  return (
    <StyledForm onSubmit={handleSubmit(onFormSubmit)}>
      <StyledGrid>
        <StyledTextField
          label={t("firstNameLabel")}
          {...register("privateName")}
          error={!!errors.privateName}
          helperText={errors.privateName?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            inputMode: "text",
            pattern: "^[\u0590-\u05FF\u200F\u200E\s]+$",
            maxLength: 100,
          }}
          onInput={(e) => {
            const input = e.target as HTMLInputElement
            input.value = input.value.replace(/[^\u0590-\u05FF\s]/g, "")
          }}
          fullWidth
        />
        <StyledTextField
          label={t("lastNameLabel")}
          {...register("sureName")}
          error={!!errors.sureName}
          helperText={errors.sureName?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            inputMode: "text",
            pattern: "^[\u0590-\u05FF\u200F\u200E\s]+$",
            maxLength: 100,
          }}
          onInput={(e) => onInputHandle(e)}
          fullWidth
        />

        <StyledTextField
          label={t("phoneLabel")}
          {...register("mobile")}
          error={!!errors.mobile}
          helperText={errors.mobile?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 10,
          }}
          onInput={handleNumericInput}
          fullWidth
        />

        <StyledTextField
          label={t("idLabel")}
          {...register("identityNumber")}
          error={!!errors.identityNumber}
          helperText={errors.identityNumber?.message}
          disabled={isSubmitting || disabled}
          fullWidth
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 9,
          }}
          onInput={handleNumericInput}
        />

        <StyledTextField
          label={t("emailLabel")}
          type="email"
          {...register("email")}
          error={!!errors.email}
          helperText={errors.email?.message}
          disabled={isSubmitting || disabled}
          fullWidth
        />

        <StyledTextField
          label={t("carNumberReportLabel")}
          {...register("licenseNumber")}
          error={!!errors.licenseNumber}
          helperText={errors.licenseNumber?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 10,
          }}
          onInput={handleNumericInput}
          fullWidth
        />

        <StyledTextField
          label={t("insuranceCompanyLabel")}
          {...register("insuranceCompanyName")}
          error={!!errors.insuranceCompanyName}
          helperText={errors.insuranceCompanyName?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            pattern: "^[\u0590-\u05FF\u200F\u200E\s]+$",
          }}
          onInput={(e) => onInputHandle(e)}
          fullWidth
        />

        <StyledTextField
          label={t("policyNumberLabel")}
          {...register("policyNumberThirdParty")}
          error={!!errors.policyNumberThirdParty}
          helperText={errors.policyNumberThirdParty?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 20,
          }}
          onInput={handleNumericInput}
          fullWidth
        />
      </StyledGrid>
      <StyledDescriptionFieldsContainer>
        <StyledTextField
          label={t("eventDescriptionLabel")}
          {...register("eventDescription")}
          error={!!errors.eventDescription}
          helperText={errors.eventDescription?.message}
          disabled={isSubmitting || disabled}
          inputProps={{
            inputMode: "text",
            pattern: "^[\u0590-\u05FF\u200F\u200E\s]+$",
            maxLength: 350,
          }}
          onInput={(e) => onInputHandle(e)}
          fullWidth
          multiline
          rows={3}
        />

        <StyledTextField
          label={t("damageDescriptionLabel")}
          {...register("damageDescription")}
          error={!!errors.damageDescription}
          helperText={errors.damageDescription?.message}
          disabled={isSubmitting || disabled}
          fullWidth
          multiline
          inputProps={{
            pattern: "^[\u0590-\u05FF\u200F\u200E\s]+$",
          }}
          onInput={(e) => onInputHandle(e)}
          rows={3}
        />
      </StyledDescriptionFieldsContainer>

      <StyledButtonContainer>
        <StyledButtonWithArrow
          type="submit"
          colorPallette="red"
          disabled={isSubmitting || disabled}
        >
          {data.accidentButtonText?.value}
        </StyledButtonWithArrow>
      </StyledButtonContainer>
      {UMBRACO__FORMS__FIELDTYPES__RECAPTCHA2__PUBLICKEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
          sitekey={UMBRACO__FORMS__FIELDTYPES__RECAPTCHA2__PUBLICKEY}
          size={"invisible"}
          hl="iw"
          tabIndex={-1}
        />
      )}
    </StyledForm>
  )
}

export default AccidentReportForm
